import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiKeyScope } from '@nucleus-commerce/api-client/admin';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Label } from '@nucleus-commerce/ui/components/label';

import { API_KEY_SCOPES } from '../schemas/api-key-form-schema';

export interface ScopePickerProps {
  value: readonly ApiKeyScope[];
  onChange: (value: ApiKeyScope[]) => void;
}

/** Checkboxes in the enum's order; none ticked means the key may call every scope. */
export function ScopePicker({ value, onChange }: ScopePickerProps) {
  const { t } = useTranslation('apiKeys');
  const baseId = useId();
  const toggle = (scope: ApiKeyScope, checked: boolean) =>
    onChange(
      checked
        ? API_KEY_SCOPES.filter((s) => s === scope || value.includes(s))
        : value.filter((s) => s !== scope),
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2 sm:grid-cols-2">
        {API_KEY_SCOPES.map((scope) => {
          const id = `${baseId}-${scope}`;
          return (
            <div key={scope} className="flex items-start gap-2">
              <Checkbox
                id={id}
                className="mt-0.5"
                checked={value.includes(scope)}
                onCheckedChange={(next) => toggle(scope, next === true)}
              />
              <Label htmlFor={id} className="flex flex-col items-start gap-0.5 font-normal">
                <code className="text-[13px]">{scope}</code>
                <span className="text-muted-foreground text-xs">
                  {t(`scopes.${scope.replace(':', '_')}`)}
                </span>
              </Label>
            </div>
          );
        })}
      </div>
      <p className="text-muted-foreground text-xs" role="status">
        {value.length === 0
          ? t('fields.scopesAll')
          : t('fields.scopesSome', { count: value.length })}
      </p>
    </div>
  );
}
